import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';

import type { User } from '@recruitee/account-types';
import { CandidateBulkActions, FullCandidateActions } from '@recruitee/candidate-data';
import type { FullCandidate } from '@recruitee/candidate-types';
import { CandidateSharedWithListComponent } from '@recruitee/candidate-ui';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { ProsemirrorBasicConfigDirective, WysiwygComponent } from '@recruitee/prosemirror/webapp';
import {
  BadgeComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  IconComponent,
  LabelComponent,
} from '@recruitee/user-interface';
import { generateUuid } from '@recruitee/utils';

export interface CandidateSharingConfirmDialogData {
  assignments: User[];
  message?: Record<string, unknown>;
  candidates: FullCandidate[];
  offerId: number;
  isBulk: boolean;
}

export interface CandidateSharingConfirmDialogResult {
  goBack: boolean;
  message: Record<string, unknown>;
}

@Component({
  selector: 'rt-candidate-sharing-confirm-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'candidate-sharing-confirm-dialog.component.html',
  styleUrls: ['./candidate-sharing-confirm-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    ButtonComponent,
    TranslatePipe,
    IconComponent,
    ReactiveFormsModule,
    LabelComponent,
    BadgeComponent,
    CandidateSharedWithListComponent,
    WysiwygComponent,
    ProsemirrorBasicConfigDirective,
  ],
})
export class CandidateSharingConfirmDialogComponent
  implements
    DialogComponentI<
      CandidateSharingConfirmDialogData,
      CandidateSharingConfirmDialogResult | null | undefined
    >
{
  public assignments: User[] = [];
  public formControl: FormControl = new FormControl<Record<string, unknown> | null>(null);
  public candidates: FullCandidate[];
  public offerId: number;
  public heading: string;
  private isBulk: boolean;

  constructor(
    private dialogRef: DialogRef,
    private cdRef: ChangeDetectorRef,
    private store: Store,
    private i18n: I18nService,
  ) {}

  public setDialogData(data: CandidateSharingConfirmDialogData, triggerUpdate: boolean): void {
    this.assignments = data.assignments ?? [];
    this.formControl.setValue(data.message ?? null);
    this.candidates = data.candidates;
    this.offerId = data.offerId;
    this.heading =
      this.candidates.length > 1
        ? this.i18n.translate(
            'app.candidate.admin_select.share_with_team.confirm_dialog.heading_multiple',
            { count: this.candidates.length },
          )
        : this.i18n.translate('app.candidate.admin_select.share_with_team.confirm_dialog.heading', {
            candidateName: this.candidates[0].name,
          });
    this.isBulk = data.isBulk;

    if (triggerUpdate) {
      this.cdRef.detectChanges();
    }
  }

  public goBack(): void {
    this.dialogRef.close({ goBack: true, message: this.formControl.value });
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public share(): void {
    if (this.isBulk) {
      this.bulkShare();
    } else {
      this.singleShare();
    }

    this.dialogRef.close({
      goBack: false,
      message: this.formControl.value,
    });
  }

  private singleShare(): void {
    this.store.dispatch(
      FullCandidateActions.shareWithTeam({
        payload: {
          candidateId: this.candidates[0].id,
          admins: this.candidates[0].admins || [],
          message: this.formControl.value,
          placementId: this.selectedOfferPlacementIds()[0],
          adminIds: this.assignments.map(user => user.id),
        },
      }),
    );
  }

  private bulkShare(): void {
    this.store.dispatch(
      CandidateBulkActions.shareWithTeam({
        payload: {
          correlationId: generateUuid(),
          params: {
            adminIds: this.assignments.map(user => user.id),
            placementIds: this.selectedOfferPlacementIds(),
            message: this.formControl.value,
          },
        },
      }),
    );
  }

  private selectedOfferPlacementIds(): number[] {
    return this.candidates.flatMap(candidate =>
      candidate.placements
        .filter(placement => (placement.offer || placement.talentPool)?.id === this.offerId)
        .map(placement => placement.id),
    );
  }
}
